import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Divider, 
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  Stack,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Warning,
  ArrowUpward,
  ArrowDownward,
  CheckCircle,
  Cancel,
  AttachMoney,
  ShowChart,
  Timeline,
  Assessment,
  KeyboardDoubleArrowRight as ViewMoreIcon,
  QuestionAnswer,
  PieChart,
  BarChart,
  Info,
  CompareArrows,
  MonetizationOn,
  Lock, // Import Lock icon
  Equalizer,
  ReportProblem,
  HealthAndSafety,
  Dashboard,
  Analytics,
  StarRate,
  Lightbulb as RecommendationsIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Category as SectorIcon,
  BarChart as MarketCapIcon,
  ViewCarousel,
  ViewQuilt,
  HelpOutline,
} from '@mui/icons-material';
import { styled, Theme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { 
  baseColors, 
  getSignalColor, 
  semanticColors, 
} from '../../theme/themesV3';
import { alpha } from '@mui/material/styles';


// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  boxShadow: `0 2px 10px ${alpha(baseColors.gray900, 0.08)}`,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 20px ${alpha(baseColors.gray900, 0.1)}`,
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
  }
}));

const SectorChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 'bold',
}));

const TimeframeCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
}));

// New styled component for disabled overlay
const DisabledOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: alpha(baseColors.gray500, 0.1), // Light grey overlay from themesV3
  backdropFilter: 'blur(1px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius, // Match parent border radius
  zIndex: 1, // Ensure it's above the content
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'not-allowed',
}));

// New styled component for rating history dots
const RatingHistoryDot = styled('div')(({ color }) => ({
  width: 4,
  height: 4,
  borderRadius: '50%',
  backgroundColor: color,
  margin: '0 1px',
}));

// New styled component for rating history tooltip container
const RatingHistoryContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 6,
  height: 10, // Keep height consistent
});

const AllocationBarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '20px',
  backgroundColor: theme.palette.grey[300],
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  position: 'relative',
  marginTop: theme.spacing(1),
}));

const AllocationBarSegment = styled(Box)<{ width: number; color: string }>(({ width, color }) => ({
  height: '100%',
  width: `${width}%`,
  backgroundColor: color,
  position: 'absolute',
  left: 0,
  top: 0,
}));

const AllocationMarker = styled(Box)<{ markerPosition: number; color: string }>(({ markerPosition, color, theme }) => ({
  position: 'absolute',
  left: `${markerPosition}%`, // Use markerPosition here
  top: '-4px',
  bottom: '-4px',
  width: '3px',
  backgroundColor: color,
  borderRadius: '1px',
  zIndex: 1,
  '&::after': { // Small circle indicator
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: color,
    border: `1px solid ${theme.palette.background.paper}`,
  }
}));

// Near your other styled components
const TrendIndicator = styled(Box)<{ trend?: string }>(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(0.5),
    //color: getSignalColor(trend?.toLowerCase() || '')
    color: trend === 'IMPROVING' 
      ? (getSignalColor('IMPROVING'))
      : trend === 'DETERIORATING'
      ? (getSignalColor('DETERIORATING'))
      : (getSignalColor('STEADY'))
  },
  '& .MuiTypography-root': {
    color: theme.palette.common.white
  }
}));


  // Helper function to get trend icon
  const getTrendIcon = (trend: string, theme: Theme) => {
    if (!trend) return null;
    
    const trendLower = trend.toLowerCase();
    if (trendLower.includes('improving') || trendLower.includes('positive') || trendLower.includes('inflow')) {
      return <ArrowUpward sx={{ bgcolor: getSignalColor(trend), color: theme.palette.secondary.contrastText, mr: 0.5, borderRadius: '50%' }} />;
    } else if (trendLower.includes('deteriorating') || trendLower.includes('negative') || trendLower.includes('outflow')) {
      return <ArrowDownward sx={{ bgcolor: getSignalColor(trend), color: theme.palette.secondary.contrastText, mr: 0.5, borderRadius: '50%' }} />;
    } else if (trendLower.includes('steady') || trendLower.includes('neutral') || trendLower.includes('flat')) {
      return <CompareArrows sx={{ bgcolor: getSignalColor(trend), color: theme.palette.secondary.contrastText, mr: 0.5, borderRadius: '50%' }} />;
    } else {
      return <HelpOutline sx={{ bgcolor: theme.palette.action.disabledBackground, color: theme.palette.secondary.contrastText, mr: 0.5, borderRadius: '50%' }} />;
    }
  };

// Add this with your other styled components
const TrendsCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  // Fallback to direct baseColors to avoid TypeScript errors
  backgroundColor: theme.palette?.custom?.bannerBg || baseColors.blue,
  color: baseColors.white,
  borderRadius: theme.shape.borderRadius,
  boxShadow: `0 4px 12px ${alpha(
    theme.palette?.custom?.shadowColor || baseColors.navy, 
    0.15
  )}`,
  position: 'relative',
  overflow: 'hidden',
  '& .MuiTypography-root': {
    color: 'inherit'
  }
}));

// --- Helper Functions ---

const formatPercent = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return `${value.toFixed(1)}%`;
};

interface MarketDashboardProps {
  marketReport: any;
  trendReports: any;
  onTickerSelect?: (ticker: string) => void;
  renderMarketRating: (rating: string) => React.ReactElement;
  renderMarketTrend: (trend: string) => React.ReactElement;
  renderMarketRegime: (regime: string) => React.ReactElement;
  onTabChange: (index: number, subIndex?: number, view?: string) => void;
}

const MarketDashboard: React.FC<MarketDashboardProps> = ({ 
  marketReport, 
  trendReports, 
  onTickerSelect,
  renderMarketRating,
  renderMarketTrend,
  renderMarketRegime,
  onTabChange
}) => {
  console.log("MarketReport:", marketReport);
  console.log("RecommendedAllocation:", marketReport?.market_report?.RecommendedAllocation);
  const theme = useTheme();
  const report = marketReport?.market_report; // Use optional chaining
  const historicalReports = marketReport?.historical_reports || {}; // Get historical reports
  const performanceData = trendReports?.Performance?.MarketAllUp?.Report;
  const riskData = trendReports?.Risk?.MarketAllUp?.Report;
  const moneyFlowData = trendReports?.['Money Flow']?.MarketAllUp?.Report;
  const sectorDataAvailable = !!report?.Sectors?.Reports; // Check if sector data exists
  const overviewDataAvailable = !!report?.MarketRating && !!report?.MarketTrend && !!report?.MarketRegime;
  const summaryDataAvailable = !!report?.MarketOverview;
  const recommendationsAvailable = !!report?.InvestmentRecommendations; // Check for recommendations data


  // If the base report is missing, show a general message
  if (!report) {
     return (
        <Alert severity="warning" sx={{ m: 2 }}>
          Market report data is currently unavailable. This might be due to your subscription level or data processing delays.
        </Alert>
     );
  }

  // Extract top recommended sectors (BUY ratings) - check availability
  const recommendedSectors = sectorDataAvailable ? (report.Sectors.Reports.filter(
    (sector: any) => sector.Rating?.includes('BULL')
  ) || []) : [];

  // Extract sectors to avoid (BEAR ratings) - check availability
  const sectorsToAvoid = sectorDataAvailable ? (report.Sectors.Reports.filter(
    (sector: any) => sector.Rating?.includes('BEAR')
  ) || []) : [];

  // Extract investment recommendations
  const investRecommendations = report.InvestmentRecommendations?.filter(
    (rec: any) => rec.RecommendationType === 'invest'
  ) || [];
  
  const reduceRecommendations = report.InvestmentRecommendations?.filter(
    (rec: any) => rec.RecommendationType === 'reduce'
  ) || [];

  // Check for recommended tickers
  const recommendedTickers = report.tickers_meeting_investment_profile || [];
  const tickersAvailable = recommendedTickers.length > 0;

  // Get MarketAllUp explanation
  const marketAllUpExplanation = report.MarketAllUp?.Report?.RatingTrendExplanation || '';
  const marketAllUpExplanationAvailable = !!marketAllUpExplanation;

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString + "T00:00:00"); // Append time to ensure correct parsing
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  // // Helper function to get status color
  // const getStatusColor = (status: string) => {
  //   status = status?.toLowerCase() || '';
    
  //   if ((status.includes('bull') || status.includes('positive') || status.includes('improving') 
  //     || status.includes('gain') || status.includes('low') || status.includes('inflow')) && (!status.includes('outflow'))) {
  //     return semanticColors.improving || theme.palette.success.main;
  //   } else if (status.includes('bear') || status.includes('negative') || status.includes('deteriorating') || status.includes('loss') || status.includes('high') || status.includes('outflow')) {
  //     return semanticColors.deteriorating || theme.palette.error.main;
  //   } else if (status.includes('neutral') || status.includes('steady') || status.includes('flat') || status.includes('top') || status.includes('bottom')) {
  //     return semanticColors.steady || theme.palette.warning.main;
  //   }
  //   return theme.palette.grey[500];
  // };

  // Helper function to render historical rating trend
  const renderHistoricalRatingTrend = (currentRating: string, ratingType: 'MarketRating' | 'MarketTrend' | 'MarketRegime') => {
    // Get dates in ascending order (oldest to newest)
    const dates = Object.keys(historicalReports).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    
    // Take up to 20 most recent dates
    const recentDates = dates.slice(Math.max(0, dates.length - 20));
    
    return (
      <RatingHistoryContainer>
        {recentDates.map((date, index) => {
          const histReport = historicalReports[date]?.market_report;
          if (!histReport) return null;
          
          const rating = histReport[ratingType];
          if (!rating) return null;
          
          return (
            <Tooltip 
              key={date} 
              title={`${formatDate(date)}: ${rating}`}
              arrow
              placement="top"
            >
              <RatingHistoryDot color={getSignalColor(rating)} />
            </Tooltip>
          );
        })}
      </RatingHistoryContainer>
    );
  };

  // New helper function to render historical trend ratings from trend reports
  const renderHistoricalTrendRating = (reportType: 'Performance' | 'Risk' | 'Money Flow', currentData: any) => {
    if (!historicalReports || Object.keys(historicalReports).length === 0) return null;
    
    // Get dates in ascending order (oldest to newest)
    const dates = Object.keys(historicalReports).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    
    // Take up to 20 most recent dates
    const recentDates = dates.slice(Math.max(0, dates.length - 20));
    
    return (
      <RatingHistoryContainer>
        {recentDates.map((date) => {
          const histReport = historicalReports[date]?.[reportType]?.MarketAllUp?.Report;
          if (!histReport) return null;
          
          const rating = histReport.Rating;
          if (!rating) return null;
          
          return (
            <Tooltip 
              key={date} 
              title={`${formatDate(date)}: ${rating}`}
              arrow
              placement="top"
            >
              <RatingHistoryDot color={getSignalColor(rating)} />
            </Tooltip>
          );
        })}
      </RatingHistoryContainer>
    );
  };

  // Helper to render disabled state
  const renderDisabledState = (message = "Data not available for your current plan.") => (
    <DisabledOverlay>
      <Lock sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.disabled">
        {message}
      </Typography>
    </DisabledOverlay>
  );

  // Then use a combination of actual and mock data
  const recommendedAllocation = report?.AllocationRecommendations || 
                               report?.MarketAllocation || 
                               report?.RecommendedAllocation;

  return (
    <Box>
      {/* Market Overview Card */}
      <StyledPaper elevation={2} sx={{ mb: 4, position: 'relative', filter: !overviewDataAvailable ? 'grayscale(80%)' : 'none', opacity: !overviewDataAvailable ? 0.6 : 1 }}>
        {!overviewDataAvailable && renderDisabledState()}
        {/* Header Row: Title left, Dates right */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <ViewCarousel sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
            Market Summary
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              <strong>Report Date:</strong> {formatDate(report.ReportDate)}
            </Typography><br />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              <strong>Data Period:</strong> {report.DataDates || 'N/A'}
            </Typography>
          </Box>
        </Box>
        {/* Main Metrics Row */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: '100%', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Market Rating</Typography>
              {renderMarketRating(report.MarketRating)}
              {Object.keys(historicalReports).length > 0 && renderHistoricalRatingTrend(report.MarketRating, 'MarketRating')}
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: '100%', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Market Trend</Typography>
              {renderMarketTrend(report.MarketTrend)}
              {Object.keys(historicalReports).length > 0 && renderHistoricalRatingTrend(report.MarketTrend, 'MarketTrend')}
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: '100%', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Market Regime</Typography>
              {renderMarketRegime(report.MarketRegime || 'Unknown')}
              {Object.keys(historicalReports).length > 0 && renderHistoricalRatingTrend(report.MarketRegime || 'Unknown', 'MarketRegime')}
            </Card>
          </Grid>
        </Grid>
        {/* Projected Market Trends Grouped Card */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12}>
            <TrendsCard>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Projected Market Trends</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Short, mid, and long-term market outlooks based on current data.
              </Typography>
              
              {/* Trend indicators */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, flex: 1, justifyContent: 'center' }}>
                
                {/* Short-term */}
                <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth for better wrapping */}  
                        <Typography variant="body1" gutterBottom sx={{ display: 'block' }}>
                        Short-term
                        </Typography>  
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(report.ProjectedMarketTrend_ShortTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Mid-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Mid-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {getTrendIcon(report.ProjectedMarketTrend_MidTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Long-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Long-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(report.ProjectedMarketTrend_LongTerm || 'STEADY', theme)}
                        </Box>
                      </Box>
              </Box>
            </TrendsCard>
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Key Metrics Row */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
        <PieChart sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
        Key Metrics
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Performance */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={2} sx={{ cursor: 'pointer', position: 'relative', filter: !performanceData ? 'grayscale(80%)' : 'none', opacity: !performanceData ? 0.6 : 1 }} onClick={() => onTabChange(2, 0, 'performance')}>
             {!performanceData && renderDisabledState("Performance data unavailable.")}
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ mr: 0.5, color: theme.palette.custom.iconSecondary }} fontSize="small" />
                  Performance
                </Typography>
                <Tooltip title="View Performance Details">
                  {/* Wrap IconButton in span to allow tooltip even when disabled */}
                  <span>
                    <IconButton size="small" onClick={() => onTabChange(2, 0, 'performance')} disabled={!performanceData}>
                      <ViewMoreIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                    Current Status
                  </Typography>
                  <Typography 
                    sx={{ 
                      color: getSignalColor(performanceData?.Rating || '')
                    }}
                    textAlign={'center'}
                  >
                    {performanceData?.Rating || 'N/A'}
                  </Typography>
                  {/* Add historical ratings for Performance */}
                  {Object.keys(historicalReports).length > 0 && 
                    renderHistoricalTrendRating('Performance', performanceData)}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                    Trend
                  </Typography>
                  {renderMarketTrend(performanceData?.Trend || 'Unknown')}
                </Box>
              </Box>
              
              <Box>
                <Box sx={{ mt: 1.5, opacity: 0.85 }}> {/* Reduced top margin and added slight opacity */}
                  <Typography variant="body2" gutterBottom color="text.secondary" align="center"> {/* Smaller variant, faded color */}
                  Projected Trends
                  </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {/* Short-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth for better wrapping */}  
                        <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
                        Short-term
                        </Typography>  
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(performanceData?.ProjectedTrend_ShortTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Mid-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Mid-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {getTrendIcon(performanceData?.ProjectedTrend_MidTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Long-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Long-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(performanceData?.ProjectedTrend_LongTerm || 'STEADY', theme)}
                        </Box>
                      </Box>
                    </Box>
                </Box>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
        
        {/* Risk Level */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={2} sx={{ cursor: 'pointer', position: 'relative', filter: !riskData ? 'grayscale(80%)' : 'none', opacity: !riskData ? 0.6 : 1 }} onClick={() => onTabChange(2, 1, 'risk-level')}>
            {!riskData && renderDisabledState("Risk data unavailable.")}
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <ReportProblem sx={{ mr: 0.5, color: theme.palette.custom.iconSecondary }} fontSize="small" />
                  Risk Level
                </Typography>
                <Tooltip title="View Risk Level Details">
                  <span>
                    <IconButton size="small" onClick={() => onTabChange(2, 1, 'risk-level')} disabled={!riskData}>
                      <ViewMoreIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                    Current Status
                  </Typography>
                  <Typography 
                    sx={{ 
                      color: getSignalColor(riskData?.Rating || '')
                    }}
                    textAlign={'center'}
                  >
                    {riskData?.Rating || 'N/A'}
                  </Typography>
                  {/* Add historical ratings for Risk */}
                  {Object.keys(historicalReports).length > 0 && 
                    renderHistoricalTrendRating('Risk', riskData)}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                    Trend
                  </Typography>
                  {renderMarketTrend(riskData?.Trend || 'Unknown')}
                </Box>
              </Box>
              
              <Box>
                <Box sx={{ mt: 1.5, opacity: 0.85 }}> {/* Reduced top margin and added slight opacity */}
                    <Typography variant="body2" gutterBottom color="text.secondary" align="center"> {/* Smaller variant, faded color */}
                    Projected Trends
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {/* Short-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth for better wrapping */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Short-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(riskData?.ProjectedTrend_ShortTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Mid-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Mid-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(riskData?.ProjectedTrend_MidTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Long-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Long-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(riskData?.ProjectedTrend_LongTerm || 'STEADY', theme)}
                        </Box>
                      </Box>
                    </Box>
                </Box>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
        
        {/* Money Flow */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={2} sx={{ cursor: 'pointer', position: 'relative', filter: !moneyFlowData ? 'grayscale(80%)' : 'none', opacity: !moneyFlowData ? 0.6 : 1 }} onClick={() => onTabChange(2, 2, 'money-flow')}>
             {!moneyFlowData && renderDisabledState("Money Flow data unavailable.")}
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <AttachMoney sx={{ mr: 0.5, color: theme.palette.custom.iconSecondary }} fontSize="small" />
                  Money Flow
                </Typography>
                <Tooltip title="View Money Flow Details">
                  <span>
                    <IconButton size="small" onClick={() => onTabChange(2, 2, 'money-flow')} disabled={!moneyFlowData}>
                      <ViewMoreIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                    Current Status
                  </Typography>
                  <Typography 
                    sx={{ 
                      color: getSignalColor(moneyFlowData?.Rating || '')
                    }}
                    textAlign={'center'}
                  >
                    {moneyFlowData?.Rating || 'N/A'}
                  </Typography>
                  {/* Add historical ratings for Money Flow */}
                  {Object.keys(historicalReports).length > 0 && 
                    renderHistoricalTrendRating('Money Flow', moneyFlowData)}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                    Trend
                  </Typography>
                  {renderMarketTrend(moneyFlowData?.Trend || 'Unknown')}
                </Box>
              </Box>
              
              <Box>
                <Box sx={{ mt: 1.5, opacity: 0.85 }}> {/* Reduced top margin and added slight opacity */}
                    <Typography variant="body2" gutterBottom color="text.secondary" align="center"> {/* Smaller variant, faded color */}
                    Projected Trends
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {/* Short-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth for better wrapping */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Short-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(moneyFlowData?.ProjectedTrend_ShortTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Mid-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Mid-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(moneyFlowData?.ProjectedTrend_MidTerm || 'STEADY', theme)}
                        </Box>
                      </Box>

                      {/* Long-term */}
                      <Box sx={{ textAlign: 'center', flexBasis: '30%', minWidth: '80px' }}> {/* Added flexBasis and minWidth */}
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Long-term
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getTrendIcon(moneyFlowData?.ProjectedTrend_LongTerm || 'STEADY', theme)}
                        </Box>
                      </Box>
                    </Box>
                </Box>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
        
        {/* Sector Health */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={2} sx={{ cursor: 'pointer', position: 'relative', filter: !sectorDataAvailable ? 'grayscale(80%)' : 'none', opacity: !sectorDataAvailable ? 0.6 : 1 }} onClick={() => onTabChange(3)}>
            {!sectorDataAvailable && renderDisabledState("Sector data unavailable.")}
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <HealthAndSafety sx={{ mr: 0.5, color: theme.palette.custom.iconSecondary }} fontSize="small" />
                  Sector Health
                </Typography>
                <Tooltip title="View Sector Details">
                  <span>
                    <IconButton size="small" onClick={() => onTabChange(3)} disabled={!sectorDataAvailable}>
                      <ViewMoreIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" align="left" gutterBottom>
                  Strong Sectors
                </Typography>
                {recommendedSectors.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'left' }}>
                    {recommendedSectors.slice(0, 2).map((sector: any, index: number) => (
                      <SectorChip 
                        key={index}
                        size="small"
                        label={sector.Classification.replace('Sector: ', '')}
                        color="success"
                      />
                    ))}
                    {recommendedSectors.length > 2 && (
                      <SectorChip 
                        size="small"
                        label={`+${recommendedSectors.length - 2} more`}
                        color="default"
                      />
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">{sectorDataAvailable ? 'None' : 'N/A'}</Typography>
                )}
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" align="left" gutterBottom>
                  Weak Sectors
                </Typography>
                {sectorsToAvoid.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'left' }}>
                    {sectorsToAvoid.slice(0, 2).map((sector: any, index: number) => (
                      <SectorChip 
                        key={index}
                        size="small"
                        label={sector.Classification.replace('Sector: ', '')}
                        color="error"
                      />
                    ))}
                    {sectorsToAvoid.length > 2 && (
                      <SectorChip 
                        size="small"
                        label={`+${sectorsToAvoid.length - 2} more`}
                        color="default"
                      />
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">{sectorDataAvailable ? 'None' : 'N/A'}</Typography>
                )}
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Market Summary - MOVED DOWN */}
      <StyledPaper elevation={2} sx={{ mb: 4, cursor: 'pointer', position: 'relative', filter: !summaryDataAvailable ? 'grayscale(80%)' : 'none', opacity: !summaryDataAvailable ? 0.6 : 1 }} onClick={() => onTabChange(7)}>
        {!summaryDataAvailable && renderDisabledState("Market summary unavailable.")}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <ViewQuilt sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
          Market Overview
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          {report.MarketOverview ? (
            <Box sx={{ 
              borderRadius: theme.shape.borderRadius,
              p: 2,
              bgcolor: theme.palette.background.paper,
              // maxHeight: 300,
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`
            }}>
              <ReactMarkdown>
                {report.MarketOverview + (marketAllUpExplanationAvailable ? 
                  ('\n**Rating explanation**\n' + marketAllUpExplanation) : '')}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography variant="body1">No market summary available.</Typography>
          )}
        </Box>

        {/* Add Market Recommended Allocation Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', mt: 3 }}>
            Market Recommended Allocation
          </Typography>
          {/* Market-Wide Analysis */}
          {report.MarketAllUp?.Report && (
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mx: -0.5 }}>
                <Box sx={{ width: { xs: '100%', sm: '66.67%' }, px: 0.5 }}>
                  <Tooltip title={`Min: ${formatPercent(report.MarketAllUp?.Report.Allocations.min)} | Ideal: ${formatPercent(report.MarketAllUp?.Report.Allocations.ideal)} | Max: ${formatPercent(report.MarketAllUp?.Report.Allocations.max)}`}>
                    <AllocationBarContainer>
                      <AllocationBarSegment
                        width={report.MarketAllUp?.Report.Allocations.max}
                        color={theme.palette.success.light} // Max range
                      />
                      <AllocationBarSegment
                        width={report.MarketAllUp?.Report.Allocations.min}
                        color={theme.palette.grey[300]} // Below min range (covered by max)
                      />
                      <AllocationMarker markerPosition={report.MarketAllUp?.Report.Allocations.min} color={theme.palette.warning.main} />
                      <AllocationMarker markerPosition={report.MarketAllUp?.Report.Allocations.ideal} color={theme.palette.success.dark} />
                      <AllocationMarker markerPosition={report.MarketAllUp?.Report.Allocations.max} color={theme.palette.error.main} />
                    </AllocationBarContainer>
                  </Tooltip>
                </Box>
                <Box sx={{ width: { xs: '100%', sm: '33.33%' }, px: 0.5, textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 1, sm: 0 } }}>
                  <Typography variant="h6" component="span" fontWeight="bold">{formatPercent(report.MarketAllUp?.Report.Allocations.ideal)}</Typography>
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}> (Ideal)</Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={2} mt={1}>
                  <Typography variant="caption">Min: {formatPercent(report.MarketAllUp?.Report.Allocations.min)}</Typography>
                  <Typography variant="caption">Max: {formatPercent(report.MarketAllUp?.Report.Allocations.max)}</Typography>
              </Stack>

            </Box>
          )}
  
        </Box>
            
        <Box sx={{ textAlign: 'right' }}>        
          <Button 
            // variant="outlined"
            sx={{ fontStyle: 'italic'}}
            //color="primary"
            //display="flex"
            endIcon={<ViewMoreIcon />}
            //alignItems="right"
            onClick={() => onTabChange(7)} // Assuming tab index 6 is Full Report
            disabled={!recommendationsAvailable} // Disable if summary is missing
          >
            Click for full report
          </Button>
        </Box>
      </StyledPaper>


      {/* NEW SECTION: Recommended Tickers */}
      {tickersAvailable && (
        <StyledPaper elevation={2} sx={{ mb: 4 }}>
          <Typography variant="h6" onClick={() => onTabChange(6)} gutterBottom sx={{ cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <StarRate sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
            Recommended Tickers
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Based on the current market conditions and your investment profile, we recommend the following tickers:
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {recommendedTickers.map((ticker: string) => (
                <Chip
                  key={ticker}
                  label={ticker}
                  onClick={() => onTickerSelect && onTickerSelect(ticker)}
                  clickable
                  sx={{ 
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: theme.palette.custom.navHoverBg,
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
          
          <Box sx={{ textAlign: 'right' }}>
            <Button 
              // variant="outlined" 
              // color="primary"
              sx={{ fontStyle: 'italic' }}
              endIcon={<ViewMoreIcon/>}
              onClick={() => onTabChange(6)}
            >
              Click for investment recommendations
            </Button>
          </Box>
        </StyledPaper>
      )}
      
      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Quick Actions
        </Typography>
        
        <Grid container spacing={2}>
          {/* Key Questions Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              fullWidth
              startIcon={<QuestionAnswerIcon />}
              onClick={() => onTabChange(1)}
              // Disable if the underlying data for Key Questions is known to be unavailable (e.g., summary)
              disabled={!recommendationsAvailable} 
              sx={{ p: 1.5, bgcolor: theme.palette.custom.navBg, color: theme.palette.custom.navText,
                '&:hover': {
                  bgcolor: theme.palette.custom.navHoverBg,
                  //color: theme.palette.custom.navSelectedText,
                }
              }}
            >
              Key Questions
            </Button>
          </Grid>
          
          {/* Market Trends Button */}
          {/* <Grid item xs={12} sm={6} md={3}>
            <Button 
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<TrendingUp />}
              onClick={() => onTabChange(2)}
              // Disable if performance, risk, or money flow data is missing
              disabled={!performanceData || !riskData || !moneyFlowData} 
              sx={{ p: 1.5 }}
            >
              Market Trends
            </Button>
          </Grid> */}
          
          {/* Sector Analysis Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              fullWidth
              // variant="outlined"
              // color="primary"
              startIcon={<SectorIcon />}
              onClick={() => onTabChange(3)}
              disabled={!sectorDataAvailable} // Disable if sector data is missing
              sx={{ p: 1.5, bgcolor: theme.palette.custom.navBg, color: theme.palette.custom.navText,
                '&:hover': {
                  bgcolor: theme.palette.custom.navHoverBg,
                  //color: theme.palette.custom.navSelectedText,
                }
              }}
            >
              Sector Analysis & Allocation
            </Button>
          </Grid>

          {/* Market Cap Analysis Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              fullWidth
              // variant="outlined"
              // color="primary"
              startIcon={<MarketCapIcon />}
              onClick={() => onTabChange(4)}
              disabled={!sectorDataAvailable} // Disable if sector data is missing
              sx={{ p: 1.5, bgcolor: theme.palette.custom.navBg, color: theme.palette.custom.navText,
                '&:hover': {
                  bgcolor: theme.palette.custom.navHoverBg,
                  //color: theme.palette.custom.navSelectedText,
                }
              }}
            >
              MarketCap Analysis & Allocation
            </Button>
          </Grid>

          {/* Allocation Recommendations Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              fullWidth
              // variant="outlined"
              // color="primary"
              startIcon={<RecommendationsIcon />}
              onClick={() => onTabChange(6)} // Assuming tab index 5 is Allocation
              // Disable if recommendation data is missing (assuming allocation relies on it)
              disabled={!recommendationsAvailable} 
              sx={{ p: 1.5, bgcolor: theme.palette.custom.navBg, color: theme.palette.custom.navText,
                '&:hover': {
                  bgcolor: theme.palette.custom.navHoverBg,
                  //color: theme.palette.custom.navSelectedText,
                }
              }}
            >
              Investment Recommendations
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MarketDashboard;